import * as React from 'react';
import clsx from 'clsx';

import { createLocators } from '@u1/locators';

import { ILocatorOverrides } from '../../../types/utility';
import useStyles, { typographyVariantsMap } from './Typography.styles';

export type TypographyVariant = keyof typeof typographyVariantsMap;

export const typographyVariants = Object.keys(typographyVariantsMap);

export const typographyTagVariants = ['p', 'span', 'div'] as const;

export const textAlignVariants = ['center', 'left', 'right'] as const;

export interface ITypographyProps extends ILocatorOverrides {
  /**
   * HTML tag for component rendering to the DOM
   * @default p
   */
  as?: typeof typographyTagVariants[number];
  /**
   * Text to be rendered
   */
  children: React.ReactNode;
  /**
   * Text alignment for block-type tags;
   * @default left

   */
  textAlign?: typeof textAlignVariants[number];
  /**
   * Typography type. body01 equals to $tp-body-01 typography token and inherits it's styles;
   * @default body01
   */
  variant?: TypographyVariant;
  /**
   * Displays text as a single string and shows ellipsis if the text is wider than parent container
   */
  hideOverflow?: boolean;
  /**
   * Prevents text to be wrapperd to the multiple strings
   */
  noWrap?: boolean;
  /**
   * Escape hatch to add custom styling via classes;
   */
  className?: string;
}

export const typographyLocators = createLocators({
  name: 'Typography',
  componentLocators: ['typography'],
});
const { locators } = typographyLocators();

const Typography: React.FC<ITypographyProps> = ({
  as: Tag = 'p',
  children,
  textAlign = 'left',
  variant = 'body01',
  hideOverflow = false,
  noWrap = false,
  className,
  locatorsOverrides,
}) => {
  const classes = useStyles();

  return (
    <Tag
      {...locators.typography()}
      {...locatorsOverrides}
      className={clsx(
        classes.root,
        classes[variant as keyof typeof classes],
        classes[textAlign],
        className,
        {
          [classes.hideOverflow]: hideOverflow,
          [classes.noWrap]: noWrap,
        },
      )}
    >
      {children}
    </Tag>
  );
};

export default Typography;
